import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { WhiteBloom } from "../components/WhiteBloom";
import { SwipeCard, useDeck } from "../components/SwipeCard";
import { memberColor, revealStat } from "../shared/reveal-card";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { COPY } from "../shared/copy";
import { getLeagueState, getLeaderboardWithMovement, type Member } from "../lib/league";
import { Avatar } from "../components/Avatar";
import { cleanProse } from "../shared/sanitize";
import type { OptionKey } from "../shared/types";

// Reveal. This is the payoff moment, so it is built to be read in order rather
// than scanned: how you did, then question by question who knew and what the
// answer actually was, then how the standings moved. Reactions stay aggregate,
// so the app never puts a name to a miss. The players do that themselves.
export function Reveal() {
  const { leagueId, dropId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  async function load() {
    setFailed(false);
    try {
      const { members, season } = await getLeagueState(leagueId!, user!.id);
      const { data: dqs } = await supabase.from("drop_questions").select("*").eq("drop_id", dropId).order("position");
      const dqIds = (dqs ?? []).map((d) => d.id);
      const { data: answers } = await supabase.from("answers").select("*").in("drop_question_id", dqIds);
      const byQ = new Map<string, any[]>();
      for (const a of answers ?? []) {
        const list = byQ.get(a.drop_question_id) ?? [];
        list.push(a); byQ.set(a.drop_question_id, list);
      }
      const board = season ? await getLeaderboardWithMovement(season.id, members as Member[]) : [];
      setData({ dqs: dqs ?? [], byQ, members, board });
    } catch {
      setFailed(true);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [dropId, user]);

  if (failed) {
    return (
      <div className="app" data-surface="light">
        <div className="header">
          <h1>Results</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card">
          <p className="error">We couldn&apos;t load these results.</p>
          <p className="muted">That is usually the connection. Try again.</p>
          <button className="btn primary" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="app" data-surface="light"><div className="loading">Loading results…</div></div>;

  // Question content stays sealed until the drop actually reveals, so an empty
  // list here means the results are not out yet. Rendering the normal screen
  // showed a confident "0/0" and told the player every question was about them.
  if (data.dqs.length === 0) {
    return (
      <div className="app" data-surface="light">
        <div className="header">
          <h1>Results</h1>
          <button className="link" onClick={() => navigate(`/league/${leagueId}`)}>Home</button>
        </div>
        <div className="card live">
          <span className="tag">Not yet</span>
          <p>
            These results aren&apos;t out. They post once everybody has finished or the play window closes,
            and your phone will let you know.
          </p>
          <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
          <button className="link" onClick={load}>Check again</button>
        </div>
      </div>
    );
  }

  // How the person reading this actually did, counting only questions they played.
  const mine = data.dqs.reduce(
    (acc: { right: number; played: number }, dq: any) => {
      const row = (data.byQ.get(dq.id) ?? []).find((r: any) => r.user_id === user!.id);
      if (!row || row.result === "subject_sitout") return acc;
      return { right: acc.right + (row.result === "correct" ? 1 : 0), played: acc.played + 1 };
    },
    { right: 0, played: 0 }
  );

  const nameOf = (id: string) =>
    (data.members as Member[]).find((m) => m.user_id === id)?.display_name ?? "Someone";

  return (
    <RevealDeck
      cards={data.dqs}
      byQ={data.byQ}
      meId={user!.id}
      mine={mine}
      nameOf={nameOf}
      onDone={() => navigate(`/league/${leagueId}/leaderboard`)}
      onHome={() => navigate(`/league/${leagueId}`)}
    />
  );
}

// The reveal itself: one member at a time, dragged away with your thumb.
//
// This used to be the whole drop on one scrolling page, which made the payoff
// something you skimmed. Now each card is one person's real update and you have
// to move it yourself to get to the next, because the swipe IS the getting
// underneath. After the last one you land on the leaderboard, everyone lit up
// together.
function RevealDeck({
  cards, byQ, meId, mine, nameOf, onDone, onHome,
}: {
  cards: any[];
  byQ: Map<string, any[]>;
  meId: string;
  mine: { right: number; played: number };
  nameOf: (id: string) => string;
  onDone: () => void;
  onHome: () => void;
}) {
  const deck = useDeck(cards);
  const dq = deck.current;

  // Everyone has been seen. Hold for a beat on the way to the standings rather
  // than yanking the screen out from under the last card.
  useEffect(() => {
    if (!deck.done) return;
    const t = window.setTimeout(onDone, 650);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck.done]);

  if (!dq) {
    return (
      <div className="app reveal-screen" data-surface="light">
        <div className="beat">
          <div className="big">That&apos;s everybody.</div>
          <p className="muted">Here&apos;s where it left the crew.</p>
        </div>
      </div>
    );
  }

  const rows: any[] = byQ.get(dq.id) ?? [];
  const eligible = rows.filter((r) => r.result !== "subject_sitout").length;
  const got = rows.filter((r) => r.result === "correct").length;
  const myRow = rows.find((r) => r.user_id === meId);
  const correct: OptionKey = dq.correct_option;
  const subjectId: string = dq.subject_user_id;
  const subjectName = subjectId ? nameOf(subjectId) : "The crew";
  const colour = memberColor(subjectId ?? dq.id);
  const nextId: string | undefined = cards[deck.index + 1]?.subject_user_id;
  const afterId: string | undefined = cards[deck.index + 2]?.subject_user_id;

  return (
    <div className="app reveal-screen" data-surface="light">
      {/* Out of the dark play and into the light. The one big contrast beat. */}
      <WhiteBloom />

      <div className="reveal-top">
        {/* Fill as you go, so you can see how much of the crew is left. */}
        <div className="reveal-dots" aria-label={`Card ${deck.index + 1} of ${cards.length}`}>
          {cards.map((c, i) => (
            <span key={c.id} className={`reveal-dot${i <= deck.index ? " on" : ""}`} />
          ))}
        </div>
        <button className="link" onClick={onHome}>Home</button>
      </div>

      <div className="deck-stage reveal-stage">
        {/* What is underneath: tinted in the next people's colours, and
            deliberately text-free. You can see there is more, you cannot read
            it yet. */}
        {afterId && (
          <div className="peekcard far" style={{ ["--peek" as any]: memberColor(afterId) }} aria-hidden="true" />
        )}
        {nextId && (
          <div className="peekcard near" style={{ ["--peek" as any]: memberColor(nextId) }} aria-hidden="true" />
        )}

        <SwipeCard
          key={dq.id}
          className="card reveal-card"
          hintLeft={COPY.reveal.next}
          hintRight={COPY.reveal.next}
          nudge={deck.index === 0}
          onSwipe={deck.advance}
        >
          <div className="reveal-body" style={{ ["--member" as any]: colour }}>
            <div className="reveal-kicker">
              <Avatar id={subjectId ?? dq.id} name={subjectName} size={26} />
              <span className="tag">{COPY.reveal.aboutLabel}</span>
            </div>

            {/* The person is the hero here, never the answer. */}
            <h2 className="reveal-name">{subjectName}</h2>

            <p className="reveal-question">{cleanProse(dq.question_text)}</p>

            <div className="reveal-answer">
              <span className="tag">{COPY.reveal.labelAnswer}</span>
              <span className="reveal-answer-value">{cleanProse(dq[`option_${correct}`])}</span>
            </div>

            {dq.reveal_context && <p className="reveal-line">{cleanProse(dq.reveal_context)}</p>}

            <span className="reveal-stat">
              {revealStat({
                eligible,
                got,
                youGotIt: myRow?.result === "correct",
                youWereSubject: subjectId === meId,
              })}
            </span>
          </div>
        </SwipeCard>
      </div>

      <div className="reveal-foot">
        <span className="muted">
          {mine.played === 0 ? "Every question was about you." : `You got ${mine.right} of ${mine.played}.`}
        </span>
        {/* Never gesture-only. */}
        <button className="btn ghost deck-next" onClick={deck.advance}>
          {deck.index === cards.length - 1 ? COPY.reveal.seeStandings : COPY.reveal.next}
        </button>
      </div>
    </div>
  );
}
